import { useState } from 'react';
import { useRouter } from 'next/router';
import { faGlobe as fasGlobe } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Checkers from '../../../helpers/class/Checkers';
import { SUPPORTED_LOCALES, fromBackendLocale } from '../../../helpers/class/RequestLocale';
import { localePrefix, useT } from '../../../helpers/i18n';
import DropdownArrow from './DropdownArrow';

interface LanguageSwitcherProps {
  // navigation partials of the page; the backend's language list is partials.lang.entries
  partials: any
  // set when rendered inside the side menu (same meaning as in HeaderMenu)
  modalDropdownClass?: string | null
  handleHide?: any
  // rendered in the header bar next to the search icon (spacing like the search icon, see property.css)
  inHeaderBar?: boolean
}

// TEMP: language switcher hidden while checking things; set back to false to show it again
const HIDE_LANGUAGE_SWITCHER = true;
// Globe icon with the site's languages. Rendered next to the search icon in the header bar (so it stays
// outside the burger menu), or inside the menu for header types that have no such slot.
export default function LanguageSwitcher ( { partials, modalDropdownClass = null, handleHide, inHeaderBar = false }: LanguageSwitcherProps ) {

  const router = useRouter()

  const t = useT()

  const [ langOpen, setLangOpen ] = useState( false );

  // languages the site offers (backend list, codes like 'EL') that this frontend can also serve
  const languageEntries = ( Checkers.isValidArray( partials?.lang?.entries ) ? partials.lang.entries : [] )
      .map( ( entry ) => ( { ...entry, locale: fromBackendLocale( entry.code ) } ) )
      .filter( ( entry ) => SUPPORTED_LOCALES.includes( entry.locale ) )

  if ( HIDE_LANGUAGE_SWITCHER || languageEntries.length < 2 ) {
    return null
  }

  const inModal = modalDropdownClass != null

  return (
      <li className={'nav-item dropdown cursor-pointer' + (inHeaderBar ? ' lang-switcher-bar' : '')}
          // opens on hover, like the other menu dropdowns
          onMouseEnter={() => setLangOpen(true)}
          onMouseLeave={() => setLangOpen(false)}
      >
        <a href="#" role="button"
           id={inModal ? 'modal-lang' : 'lang'}
           className={'d-flex nav-link lang-toggle' + (langOpen ? ' show' : '')}
           aria-label={t('a11y.language')} title={t('a11y.language')}
           aria-haspopup="true" aria-expanded={langOpen ? 'true' : 'false'}
           onClick={(e) => {
             e.preventDefault()
             // a mouse/touch click only opens: on touch screens the tap fires mouseenter first, so
             // toggling would close it again at once. Keyboard (Enter, e.detail === 0) toggles.
             setLangOpen(e.detail === 0 ? !langOpen : true)
           }}
        >
          <FontAwesomeIcon icon={fasGlobe} size="2x"/>
        </a>
        <ul className={(inModal ? 'dropdown-menu dropdown-lang-menu ' + modalDropdownClass : 'dropdown-menu dropdown-lang-menu shadow') + (langOpen ? ' show' : '')}
            aria-labelledby={inModal ? 'modal-lang' : 'lang'}>
          {
            !inModal &&
            <DropdownArrow/>
          }
          {languageEntries.map((entry, key) => (
              <li className={key == 0 ? 'dropdown-ul-item mt-20px' : 'dropdown-ul-item mt-3'} key={'lang-' + entry.locale}>
                {/* a full page load (plain href, not next/link): components that load their data once on mount
                    and the redux store would otherwise keep showing the previous language */}
                <a className={'dropdown-item' + (entry.locale === router.locale ? ' fw-bold' : '')}
                   href={(localePrefix(entry.locale) + (router.asPath === '/' ? '' : router.asPath)) || '/'}
                   lang={entry.locale} hrefLang={entry.locale}
                   aria-current={entry.locale === router.locale ? 'true' : undefined}
                   onClick={() => {
                     setLangOpen(false)
                     if (typeof handleHide === 'function') {
                       handleHide()
                     }
                   }}>{entry.name}</a>
              </li>
          ))}
        </ul>
      </li>
  )
}
